/**
 * Report drafts: listing, loading, saving, and moving through the workflow.
 *
 * One module per resource, so the set of things that can be done to a draft is
 * readable in one screen rather than spread through a file that also knows
 * about exports, comments and upstream data.
 *
 * Every function here returns the resource itself rather than the envelope it
 * arrived in. Callers want a draft, not a `{ draft }`, and unwrapping it once
 * here means no component ever has to know the envelope exists.
 */

import { http, type RequestOptions } from '@/api/http'
import type {
  DraftFull,
  DraftPatch,
  DraftSummary,
  NewDraft,
  TransitionRequest,
} from '@/domain/draft'

interface DraftListEnvelope {
  drafts: DraftSummary[]
}

interface DraftEnvelope {
  draft: DraftFull
}

interface DeletedEnvelope {
  deleted: boolean
}

/**
 * Every draft the caller can see: their own, plus those shared with them.
 *
 * The server decides what that means. Nothing is filtered here, because a
 * filter applied in the browser is a filter an attacker can remove.
 */
export async function listDrafts(options: RequestOptions = {}): Promise<DraftSummary[]> {
  const { drafts } = await http.get<DraftListEnvelope>('/reportDrafts', options)
  return drafts
}

/** One draft with its document and history. */
export async function loadDraft(id: number, options: RequestOptions = {}): Promise<DraftFull> {
  const { draft } = await http.get<DraftEnvelope>(`/reportDrafts/${id}`, options)
  return draft
}

/** Start a new draft. The caller becomes its owner. */
export async function createDraft(
  draft: NewDraft,
  options: RequestOptions = {},
): Promise<DraftFull> {
  const created = await http.post<DraftEnvelope>('/reportDrafts', draft, options)
  return created.draft
}

/**
 * Save changes to a draft.
 *
 * Omitted fields are left alone, so a rename does not have to send the whole
 * document alongside it.
 */
export async function saveDraft(
  id: number,
  patch: DraftPatch,
  options: RequestOptions = {},
): Promise<DraftFull> {
  const saved = await http.put<DraftEnvelope>(`/reportDrafts/${id}`, patch, options)
  return saved.draft
}

/** Delete a draft. Only its owner can. */
export async function deleteDraft(id: number, options: RequestOptions = {}): Promise<boolean> {
  const { deleted } = await http.delete<DeletedEnvelope>(`/reportDrafts/${id}`, options)
  return deleted
}

/**
 * Move a draft through the review workflow: submit, approve, reject, issue or
 * reopen.
 *
 * Refusals are distinguishable at the call site: 409 when the move is not
 * available from the current state or a precondition failed, 403 when the
 * caller lacks the standing to make it. Those need different things said to
 * the user, which is why they are not collapsed into one failure.
 */
export async function transitionDraft(
  id: number,
  request: TransitionRequest,
  options: RequestOptions = {},
): Promise<DraftFull> {
  const moved = await http.post<DraftEnvelope>(`/reportDrafts/${id}/transition`, request, options)
  return moved.draft
}

/**
 * Carry a draft into the next period, as a new draft.
 *
 * The original is untouched: last year's approved report stays exactly as it
 * was signed off, which is the entire point of keeping it.
 */
export async function rollForward(id: number, options: RequestOptions = {}): Promise<DraftFull> {
  const rolled = await http.post<DraftEnvelope>(
    `/reportDrafts/${id}/rollForward`,
    undefined,
    options,
  )
  return rolled.draft
}
